import React, { useEffect, useRef, useState } from "react";

import { Form } from "react-final-form";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { FormattedMessage } from "@mnemonic/intl";
import Link from "@mnemonic/mui/Link";

import ContactUsDialog from "../../Components/ContactUsDialog";
import { TextField } from "../../Components/MuiFinalForm";
import { SubmitButton } from "../../Components/SubmitButton";
import { UsernameTextField } from "../../Components/UsernameTextField";
import { useActivateEnrollmentMutation } from "../../Redux/Service/authenticateApi";
import { commonMessages } from "../../commonMessages";
import { validateEmptyField } from "../Login/utils/validate";
import { type IEnrollUsernamePassword } from "./useVerifyUsernamePassword";
import { mapErrors } from "./utils";

const getBoxStyles = (showForm: boolean) => ({
  opacity: showForm ? 1 : 0,
  transition: "opacity 0.5s ease",
  pointerEvents: showForm ? "auto" : "none",
});

const formatEnrollmentCode = (value: string) => {
  //If the input string is 9 consecutive numbers, hyphens are added between each set of 3 (xxx-xxx-xxx)

  const regexpUnhyphenated = /\d{9}/; //Exactly 9 numbers (xxxxxxxxx)

  if (value.match(regexpUnhyphenated)) {
    return value.substring(0, 3) + "-" + value.substring(3, 6) + "-" + value.substring(6, 9);
  }

  return value;
};

export const StepVerifyUser = ({
  domain,
  autoSubmit,
  enrollFlowState,
  setEnrollFlowState,
}: IEnrollUsernamePassword) => {
  const [activateEnrollment, { isError, isUninitialized }] = useActivateEnrollmentMutation();
  const hasSubmitted = useRef(false);
  let showForm = !autoSubmit || (autoSubmit && !isUninitialized && isError);
  const userName = enrollFlowState.userName;
  const enrollmentCode = enrollFlowState.enrollmentCode;

  const [open, setOpen] = useState(false);

  return (
    <Box sx={getBoxStyles(showForm)}>
      <ContactUsDialog {...{ open, onClose: () => setOpen(false) }} />
      <Typography variant="body1">
        <FormattedMessage
          id="login.verify.verifyUser.info"
          defaultMessage="To enroll your user, please enter username and enrollment code from your enrollment e-mail"
        />
      </Typography>
      <Form
        initialValues={{ userName, enrollmentCode }}
        onSubmit={async ({ userName, enrollmentCode }) => {
          try {
            const { data } = await activateEnrollment({
              criteria: { domain, userName, enrollmentCode: formatEnrollmentCode(enrollmentCode) },
            }).unwrap();
            return setEnrollFlowState({
              //Updating username and enrollment code to allow resending of SMS in step 2 (VerifySMSCode).
              userName: userName,
              enrollmentCode: formatEnrollmentCode(enrollmentCode),

              step: { kind: "verifySmsCode" },
              activationCookie: data?.activationCookie,
              userId: data.userID,
            });
          } catch (e) {
            showForm = true; // Show form after autosubmit in case of issues (e.g. expired code)
            return mapErrors(e, ["enrollmentCode", "userName"]);
          }
        }}
        render={({ handleSubmit, submitError }) => {
          // Auto-submit logic using useEffect, only submits once
          useEffect(() => {
            if (autoSubmit && !hasSubmitted.current) {
              hasSubmitted.current = true; // Set the flag to true to prevent re-submission
              handleSubmit();
            }
          }, [autoSubmit, handleSubmit]);

          return (
            <form onSubmit={handleSubmit}>
              <Box sx={(theme) => ({ my: theme.spacing(3) })}>
                <UsernameTextField name="userName" autoFocus={true} />
              </Box>
              <Box sx={(theme) => ({ mb: theme.spacing(1) })}>
                <TextField
                  fullWidth
                  label={<FormattedMessage {...commonMessages.EnrollmentCode} />}
                  name="enrollmentCode"
                  type="text"
                  validate={(value: string) => validateEmptyField(value)}
                />
                <Typography variant="body2" mt={2}>
                  <FormattedMessage
                    id="login.verify.missing"
                    defaultMessage="Haven’t received e-mail or code expired?"
                  />
                  <Link onClick={() => setOpen(true)} style={{ color: "inherit", marginLeft: "4px" }}>
                    <FormattedMessage {...commonMessages.ContactUs} />
                  </Link>
                </Typography>
              </Box>
              {submitError && (
                <Alert variant="outlined" severity="error">
                  {submitError}
                </Alert>
              )}
              <SubmitButton>
                <FormattedMessage id="login.verify.button.submit" defaultMessage="verify enrollment code" />
              </SubmitButton>
            </form>
          );
        }}
      />
    </Box>
  );
};
